// A tap on one koi of a group photo on a product card opens the listing
// with that koi selected (?variant=), as tapping it on the product page
// selects it there.
//
// Cards rendered with koi_pick (snippets/card-product.liquid) list each koi
// still for sale on .card__media[data-koi-pick]: its padded outline, body
// line and painted letter box, in fractions of the photo. The card's link is
// pointed at the koi under the pointer as the pointer goes down, and, with a
// mouse, as it moves, so the status bar, a middle-click and "Open in new
// tab" all get that koi; off the koi it points back at the listing. Anywhere
// else on the card, a koi already sold, or the keyboard opens the listing as
// before. With a mouse, the koi's painted letter is ringed while over it, in
// the ring the per-koi cards and the product page draw.
//
// Only the card's own link (CardLink-) changes. ck-koi-trail.js reads the
// listing's URL from the card's first link, which is never touched here.
(() => {
  if (window.__CK_CARD_KOI_PICK__) return;
  window.__CK_CARD_KOI_PICK__ = true;

  // letter|variant|x|y|turn|length|cx|cy|d|outline, outline in thousandths
  function kois(media) {
    if (!media._ckKois) {
      media._ckKois = (media.dataset.koiPick || '')
        .split(';')
        .filter(Boolean)
        .map((entry) => {
          const [letter, variant, x, y, turn, length, cx, cy, d, path = ''] = entry.split('|');
          return {
            letter,
            variant,
            x: Number(x),
            y: Number(y),
            turn: Number(turn) || 0,
            length: Number(length),
            label: cx === '' ? null : { cx: Number(cx), cy: Number(cy), d: Number(d) },
            points: path
              .replace(/[MZ]/g, '')
              .split('L')
              .map((pair) => pair.trim().split(/\s+/).map((n) => Number(n) / 1000)),
          };
        });
    }
    return media._ckKois;
  }

  // The photo as drawn, in client px: the img box less the bars
  // object-fit: contain leaves (or plus what cover crops), centred as the
  // card CSS has it. The box already carries any hover transform.
  function photoBox(img) {
    const box = img.getBoundingClientRect();
    const iw = Number(img.getAttribute('width')) || img.naturalWidth;
    const ih = Number(img.getAttribute('height')) || img.naturalHeight;
    if (!box.width || !box.height || !iw || !ih) return null;
    const fit = getComputedStyle(img).objectFit;
    let width = box.width;
    let height = box.height;
    if (fit === 'contain' || fit === 'cover') {
      const scale = (fit === 'contain' ? Math.min : Math.max)(box.width / iw, box.height / ih);
      width = iw * scale;
      height = ih * scale;
    }
    return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
  }

  function insideOutline(points, x, y) {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      if (
        points[i][1] > y !== points[j][1] > y &&
        x < ((points[j][0] - points[i][0]) * (y - points[i][1])) / (points[j][1] - points[i][1]) + points[i][0]
      ) {
        inside = !inside;
      }
    }
    return inside;
  }

  // Distance from (x, y) to the koi's body line (its crop's middle, turned
  // `turn` degrees clockwise from head down, `length` photo heights long),
  // in photo heights. Same measure as main-product.liquid's koiBodyDistance.
  function bodyDistance(koi, x, y, aspect) {
    const half = koi.length / 2;
    const along = ((90 - koi.turn) * Math.PI) / 180;
    const dx = Math.cos(along) * half;
    const dy = Math.sin(along) * half;
    const qx = (x - koi.x) * aspect;
    const qy = y - koi.y;
    const t = Math.max(-1, Math.min(1, (qx * dx + qy * dy) / (dx * dx + dy * dy || 1)));
    return Math.hypot(qx - t * dx, qy - t * dy);
  }

  // The letter's ring, d in photo widths. Radius in photo heights.
  function letterRing(label, aspect) {
    return { cx: label.cx, cy: label.cy, r: (label.d / 2) * aspect };
  }

  // The koi at (clientX, clientY): one whose letter ring holds the point,
  // else one whose padded outline does, the nearer body line where outlines
  // overlap.
  function koiAt(media, photo, clientX, clientY) {
    const x = (clientX - photo.left) / photo.width;
    const y = (clientY - photo.top) / photo.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    const aspect = photo.width / photo.height;
    let best = null;
    let bestDistance = Infinity;
    for (const koi of kois(media)) {
      if (koi.label) {
        const ring = letterRing(koi.label, aspect);
        if (Math.hypot((x - ring.cx) * aspect, y - ring.cy) <= ring.r) return koi;
      }
      if (!insideOutline(koi.points, x, y)) continue;
      const distance = bodyDistance(koi, x, y, aspect);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = koi;
      }
    }
    return best;
  }

  // The card an event is on, when it lands on the card's own link (the
  // stretched link over the photo) of a koi_pick card.
  function cardAt(target) {
    const link = target instanceof Element ? target.closest('a[href]') : null;
    if (!link || !link.id.startsWith('CardLink-')) return null;
    const card = link.closest('.card-wrapper');
    const media = card && card.querySelector('.card__media[data-koi-pick]');
    const img = media && media.querySelector('.media > img');
    return img ? { link, media, img } : null;
  }

  function pick(event) {
    const card = cardAt(event.target);
    if (!card) return null;
    const photo = photoBox(card.img);
    card.photo = photo;
    card.koi = photo ? koiAt(card.media, photo, event.clientX, event.clientY) : null;
    return card;
  }

  // Points the card's link at the koi, or back at the listing. The query is
  // rebuilt with the koi's variant SET, not appended: on a filtered grid the
  // link may already carry Shopify's ?variant= and the first one wins.
  function point(link, koi) {
    if (link.dataset.ckListingHref === undefined) link.dataset.ckListingHref = link.getAttribute('href');
    let href = link.dataset.ckListingHref;
    if (koi) {
      const url = new URL(href, location.href);
      url.searchParams.set('variant', koi.variant);
      href = url.pathname + url.search + url.hash;
    }
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  }

  function light(card) {
    let ring = card.media.querySelector(':scope > .card-koi-pick-ring');
    const label = card.koi && card.koi.label;
    if (!label) {
      if (ring) ring.classList.remove('is-on');
      return;
    }
    if (!ring) {
      ring = document.createElement('span');
      ring.className = 'card-koi-mark card-koi-pick-ring';
      ring.setAttribute('aria-hidden', 'true');
      card.media.appendChild(ring);
    }
    const box = card.media.getBoundingClientRect();
    const { photo } = card;
    ring.style.left = `${photo.left - box.left + label.cx * photo.width}px`;
    ring.style.top = `${photo.top - box.top + label.cy * photo.height}px`;
    ring.style.width = `${label.d * photo.width}px`;
    ring.classList.add('is-on');
  }

  // ---- mouse: follow the pointer ----

  let hovered = null;
  function hover(card) {
    if (hovered && (!card || hovered.link !== card.link)) {
      point(hovered.link, null);
      light({ media: hovered.media, koi: null });
    }
    hovered = card;
    if (card) {
      point(card.link, card.koi);
      light(card);
    }
  }

  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('mousemove', (event) => hover(pick(event)), { passive: true });
    document.addEventListener('mouseout', (event) => {
      if (!event.relatedTarget) hover(null); // left the window
    });
    window.addEventListener('scroll', () => hover(null), { passive: true });
  }

  // ---- any pointer: settle the link as it is pressed ----

  function onPress(event) {
    const card = cardAt(event.target);
    if (!card) return;
    // keyboard activation (detail 0) has no point on the photo: the listing
    if (event.type === 'click' && event.detail === 0) {
      point(card.link, null);
      return;
    }
    const photo = photoBox(card.img);
    point(card.link, photo ? koiAt(card.media, photo, event.clientX, event.clientY) : null);
  }
  document.addEventListener('pointerdown', onPress, true);
  document.addEventListener('click', onPress, true);
  document.addEventListener('auxclick', onPress, true);
  document.addEventListener('contextmenu', onPress, true);
})();
